import moment from "moment-business-days-sk";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useModalStyle from "@hooks/admin/useModalStyle";
import { FormControl, MenuItem, Select } from "@material-ui/core";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import Modal from "@material-ui/core/Modal";
import { color, opacity } from "@runwayhealth/runway-components-react";
import { updateCaseShipping } from "@store/admin/adminAction";
import { caseObject } from "@store/cases/casesSlice";
import { Case, ShippingMethods } from "@types-runway/case";
import { Condition } from "@types-runway/condition";
import { RootState } from "@types-runway/state";
import { toastWarn } from "@utils/utils";

import { getInvoiceIdLink, getMdiLink } from "./CaseSearch";
import CaseAddress from "./components/CaseAddress";
import ConditionsTable from "./components/ConditionsTable";

interface IProps {
  cases: Case[];
}

const CaseResults = ({ cases }: IProps) => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const { useStyles } = useModalStyle();
  useEffect(() => {
    setScreenWidth(window.innerWidth);
  }, [window.innerWidth]);

  const dispatch = useDispatch();
  const isCaseShippingUpdating = useSelector<RootState, boolean>(
    (state) => state.admin.isCaseShippingUpdating
  );
  const orderActionsInFlight = useSelector<RootState, Record<string, boolean>>(
    (state) => state.admin.orderActionsInFlight
  );

  const shippingMethodAlert = (case_: Case, newShippingMethod: ShippingMethods) => {
    const { id, caseId, CurexaOrder } = case_;
    if (CurexaOrder?.id && orderActionsInFlight[CurexaOrder.id.toString()]) {
      toastWarn(
        "Action already in progress",
        `Case ${caseId}'s order already has a shipping action in flight. Please wait for it to finish.`
      );
      return;
    }
    if (
      confirm(
        `Are you sure you want to set ${newShippingMethod} shipping method to Case: ${caseId}?`
      )
    ) {
      dispatch(
        updateCaseShipping({
          caseId: id,
          shipping_method: newShippingMethod,
          orderId: CurexaOrder?.id,
        })
      );
    }
  };

  const [modal, setModal] = useState(false);
  const [conditionTableIsOpen, setConditionTableIsOpen] = useState(false);
  const [caseAddressIsOpen, setCaseAddressIsOpen] = useState(false);
  const [caseConditions, setCaseConditions] = useState<Condition[]>([]);
  const [selectedCase, setSelectedCase] = useState<Case>(caseObject.data);
  const classes = useStyles(screenWidth);
  return (
    <div>
      <table>
        <thead>
          <tr>
            <th>Case ID</th>
            <th>MDI Case ID</th>
            <th>Order Id</th>
            <th>Payment Status</th>
            <th>Conditions</th>
            <th>Case Status</th>
            <th>Order Status</th>
            <th>Departure Date</th>
            <th>Shipping Method</th>
            <th>Updated At</th>
          </tr>
        </thead>
        <tbody>
          {cases.map((case_, rowIndex) => {
            return (
              <tr key={`case-${case_.id}-${rowIndex}`}>
                <td>{case_.id}</td>
                <td>{getMdiLink(case_.caseId || "N/A")}</td>
                <td>{case_.CurexaOrder?.order_id || "N/A"}</td>
                <td>{getInvoiceIdLink(case_.invoiceId || "❌")}</td>
                <td>
                  <button
                    onClick={() => {
                      setCaseConditions(case_.conditions);
                      setModal(true);
                      setConditionTableIsOpen(true);
                    }}
                  >
                    Show conditions
                  </button>
                </td>
                <td>{case_.case_status || "N/A"}</td>
                {/*
                  NOTE: CurexaOrder is a SINGULAR hasOne hydration — it may surface any one of
                  the case's orders (e.g. a stale/cancelled order after a reissue) with no hint a
                  newer one exists. Showing ALL orders needs a backend hasOne->hasMany change; that
                  is deferred to the revamp. Frontend mitigation below: de-emphasize an inactive
                  order and point the admin to Order Search for the full list.
                */}
                {(() => {
                  const orderStatus = case_.CurexaOrder?.status;
                  const isInactive = orderStatus === "cancelled" || orderStatus === "error";
                  return (
                    <td>
                      {orderStatus ? (
                        <span style={isInactive ? { color: color.neutral.medium } : undefined}>
                          {orderStatus}
                        </span>
                      ) : (
                        "N/A"
                      )}
                      {orderStatus === "cancelled" && (
                        <div style={{ color: color.neutral.medium, fontSize: "0.75em" }}>
                          This order was cancelled. There may be a newer one — search this Case ID in
                          Order Search to see it.
                        </div>
                      )}
                    </td>
                  );
                })()}
                <td>
                  {case_.trip?.from
                    ? moment(new Date(case_.trip.from)).format("dddd, MMMM Do YYYY")
                    : "N/A"}
                </td>
                <FormControl fullWidth>
                  <Select
                    value={case_.shipping_method ?? ShippingMethods.STANDARD}
                    label="Shipping Method"
                    SelectDisplayProps={{ "aria-label": "Shipping Method" }}
                    disabled={
                      isCaseShippingUpdating ||
                      Boolean(
                        case_.CurexaOrder?.id &&
                          orderActionsInFlight[case_.CurexaOrder.id.toString()]
                      )
                    }
                    onChange={(evt: React.ChangeEvent<{ value: unknown }>) => {
                      const chosenShippingMethod: ShippingMethods = evt.target
                        .value as ShippingMethods;
                      shippingMethodAlert(case_, chosenShippingMethod);
                    }}
                  >
                    {Object.keys(ShippingMethods).map((shippingMethod: string) => {
                      const key: keyof typeof ShippingMethods =
                        shippingMethod as keyof typeof ShippingMethods;
                      return (
                        <MenuItem value={ShippingMethods[key]}>{ShippingMethods[key]}</MenuItem>
                      );
                    })}
                  </Select>
                  <button
                    onClick={() => {
                      setSelectedCase(case_);
                      setModal(true);
                      setCaseAddressIsOpen(true);
                    }}
                  >
                    Shipping Address
                  </button>
                </FormControl>
                <td>{case_.updatedAt}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Modal
        open={modal}
        onClose={() => {
          setModal(false);
          setConditionTableIsOpen(false);
          setCaseAddressIsOpen(false);
        }}
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          style: { backgroundColor: `${color.neutral.darkest}`, opacity: `${opacity.semiOpaque}` },
        }}
      >
        <Fade in={modal}>
          <div className={classes.paper}>
            {" "}
            {conditionTableIsOpen ? (
              <ConditionsTable conditions={caseConditions} />
            ) : (
              <>
                {caseAddressIsOpen && (
                  <CaseAddress
                    case_={selectedCase}
                    closeModal={() => {
                      setModal(false);
                      setCaseAddressIsOpen(false);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </Fade>
      </Modal>
    </div>
  );
};

export default CaseResults;
